import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import CubMascot from './CubMascot.jsx';
import CyberCubLogo from './CyberCubLogo.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import HelpBot from './HelpBot.jsx';
import { Avatar } from './ui.jsx';

function Brand({ to }) {
  return (
    <Link to={to} className="brand" aria-label="CyberCub home">
      <CyberCubLogo size={38} className="brand-mark" />
      <span className="brand-name">
        Cyber<span className="brand-name-alt">Cub</span>
      </span>
    </Link>
  );
}

function UserMenu({ playful }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const roleLabel = { student: 'Cub Explorer', admin: 'School Admin', superadmin: 'Superadmin' }[
    user.role
  ];

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="usermenu">
      <button
        type="button"
        className="usermenu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {playful ? (
          <CubMascot avatar={user.avatar} size={40} mood="happy" />
        ) : (
          <Avatar name={user.fullName} tone={user.role === 'superadmin' ? 'grape' : 'sky'} size={38} />
        )}
        <span className="usermenu-text">
          <span className="usermenu-name">{user.fullName}</span>
          <span className="usermenu-role">{roleLabel}</span>
        </span>
        <span className="usermenu-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <>
          <button
            type="button"
            className="usermenu-scrim"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          />
          <div className="usermenu-panel anim-pop" role="menu">
            <div className="usermenu-head">
              <div className="usermenu-name">{user.fullName}</div>
              <div className="small faint">
                {user.userId}
                {user.schoolName ? ` · ${user.schoolName}` : ''}
              </div>
            </div>
            {user.role === 'student' && (
              <Link to="/learn/me" className="usermenu-item" role="menuitem" onClick={() => setOpen(false)}>
                🐻 My profile
              </Link>
            )}
            <Link to="/cyberpath" className="usermenu-item" role="menuitem" onClick={() => setOpen(false)}>
              🧭 CyberPath
            </Link>
            <Link to="/account" className="usermenu-item" role="menuitem" onClick={() => setOpen(false)}>
              🔑 Change my password
            </Link>
            <button type="button" className="usermenu-item danger" role="menuitem" onClick={signOut}>
              👋 Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One shell for all three roles. `nav` is a list of {to, label, icon};
 * `playful` switches on the kid-facing styling (bigger, brighter).
 */
export default function Layout({ nav = [], playful = false, children, wide = false }) {
  return (
    <div className={`shell ${playful ? 'shell-play' : 'shell-work'}`}>
      <header className="topbar">
        <div className="topbar-inner">
          <Brand to={nav[0]?.to ?? '/'} />

          {nav.length > 0 && (
            <nav className="topnav" aria-label="Main">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `topnav-link ${isActive ? 'is-active' : ''}`}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          )}

          <div className="topbar-actions">
            <ThemeToggle />
            <UserMenu playful={playful} />
          </div>
        </div>
      </header>

      <main className={`page ${wide ? 'page-wide' : ''}`}>{children}</main>

      <footer className="footer">
        <span className="footer-brand">
          <CyberCubLogo size={22} />
          CyberCub · Learn to stay safe online
        </span>
        <span className="faint small">Ages 7–12 · Junior &amp; Ethical Hacker tracks</span>
      </footer>

      <HelpBot />
    </div>
  );
}
